import React from 'react';
import { Card, Button, Tag } from 'antd-v5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import dayjs from 'dayjs';
// eslint-disable-next-line import/no-extraneous-dependencies
import PropTypes from 'prop-types';

/**
 * EventCard component for displaying event summary
 * Used in Events list page
 */
const EventCard = ({ event, isPast, isInProgress, onClick, onLinksClick }) => {
  const { name, startDate, endDate, audienceCount, clientsWithAppointmentsCount, salesAmount } = event;

  const eventStartDate = startDate;
  const eventEndDate = endDate;
  const audience = event.audience?.audienceCount || audienceCount || 0;
  const appointments = clientsWithAppointmentsCount || 0;
  const sales = salesAmount || 0;

  // Format date range
  const formatDateRange = () => {
    if (!eventStartDate) return 'No dates set';

    const start = dayjs(eventStartDate);
    const end = eventEndDate ? dayjs(eventEndDate) : null;

    if (!end || start.isSame(end, 'day')) {
      return start.format('ddd MMM D, YYYY');
    }

    // Check if same month and year
    if (start.isSame(end, 'month')) {
      return `${start.format('ddd MMM D')} - ${end.format('ddd MMM D, YYYY')}`;
    }

    return `${start.format('ddd MMM D, YYYY')} - ${end.format('ddd MMM D, YYYY')}`;
  };

  // Format currency
  const formatCurrency = amount => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleLinksClick = e => {
    e.stopPropagation();
    if (onLinksClick) {
      onLinksClick(event);
    }
  };

  return (
    <Card className="event-card" hoverable onClick={onClick}>
      <div className="event-card-content">
        {/* Header */}
        <div className="event-card-header">
          <div className="event-card-title-row">
            <h4 className="event-card-title">{name || 'Untitled Event'}</h4>
            {isInProgress && (
              <Tag color="success" className="event-card-badge">
                In Progress
              </Tag>
            )}
          </div>
          <p className="event-card-date">{formatDateRange()}</p>
        </div>

        {/* Stats */}
        <div className="event-card-stats">
          <div className="event-card-stat">
            <span className="event-card-stat-label">Audience</span>
            <span className="event-card-stat-value">{audience.toLocaleString()}</span>
          </div>
          <div className="event-card-divider" />
          <div className="event-card-stat">
            <span className="event-card-stat-label">Appointments</span>
            <span className="event-card-stat-value">{appointments.toLocaleString()}</span>
          </div>
          {isPast && (
            <>
              <div className="event-card-divider" />
              <div className="event-card-stat">
                <span className="event-card-stat-label">Sales</span>
                <span className="event-card-stat-value">{formatCurrency(sales)}</span>
              </div>
            </>
          )}
        </div>

        {/* Event Links Button - hidden for past events */}
        {onLinksClick && !isPast && (
          <div className="event-card-links-container">
            <Button
              type="text"
              size="small"
              icon={<FontAwesomeIcon icon={['fal', 'link']} />}
              onClick={handleLinksClick}
              className="event-card-links-btn"
            >
              Event Links
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    startDate: PropTypes.string,
    endDate: PropTypes.string,
    audienceCount: PropTypes.number,
    clientsWithAppointmentsCount: PropTypes.number,
    salesAmount: PropTypes.number,
  }).isRequired,
  isPast: PropTypes.bool,
  isInProgress: PropTypes.bool,
  onClick: PropTypes.func,
  onLinksClick: PropTypes.func,
};

EventCard.defaultProps = {
  isPast: false,
  isInProgress: false,
  onClick: () => {},
  onLinksClick: null,
};

export default EventCard;
